// ─────────────────────────────────────────────────────────────────────────────
// The row-operation matrix of the doctor's پذیرش (PM-0518) — what the doctor may
// do to ONE appointment row beyond «ثبت حضور», «ویرایش نوبت» and «شروع ویزیت».
//
// These are the clinic desk's non-money ops (clinic `src/screens/Reception/
// rowOps.js`) brought to the practitioner's own day sheet, ruled by
// docs/DOCTOR_RECEPTION_ROW_OPS_PLAN.md §4. ONE pure module for both surfaces —
// the desktop menu and the phone sheet — so the two cannot disagree about which
// op a row offers. No hooks, no JSX: each surface maps `id` to its own control.
//
// Display only. The doctor BFF decides every one of these again when it writes
// (own row, status, the clinic's grant, money, subscription); the matrix exists
// so the doctor is never offered an op that would end in a refusal.
//
// The gates, in words:
//   • `grant` — «لغو نوبت» and «عدم‌حضور» take a booking OUT of the clinic's
//     diary, so they follow the grant that lets this doctor put one in
//     (`canBookDirectly`). A row that names no clinic is the doctor's own diary.
//   • `writable` — a clinic whose subscription lapsed is read-only; only the
//     cancel, which RELEASES a booking, stays offered (as at the desk).
//   • `isToday` — arriving, being next and going back to the room happen in the
//     present tense. «عدم‌حضور» needs the day to have come (today or past).
//   • `patientId` — a «ثبت سریع» row has only the desk's label: nothing that
//     needs a patient file (arrival, the next booking) is offered on it.
// ─────────────────────────────────────────────────────────────────────────────

export const LIVE_STATUSES = new Set(['scheduled', 'arrived', 'in_visit']);

/**
 * @param {object} args
 * @param {object}  args.apt        the appointment row
 * @param {boolean} args.isToday    is the sheet showing today (on the clinic's clock)?
 * @param {boolean} args.isPast     is it showing a day before today?
 * @param {number}  [args.queuePos] the row's place in the room queue (1 = next)
 * @param {Array}   [args.clinics]  `myBookingClinics` rows: { clinicId, canBookDirectly, readOnly }
 * @param {boolean} [args.canBook]  may the doctor book at all (any booking clinic)?
 * @returns {Array<{id: string, danger: boolean}>} in display order
 */
export function receptionRowOps({ apt, isToday = false, isPast = false, queuePos = null, clinics = [], canBook = false }) {
  if (!apt?.id) return [];
  const status = apt.status;
  const clinic = apt.clinicId ? (clinics || []).find((c) => c.clinicId === apt.clinicId) : null;
  // A row that names no clinic is the doctor's own diary; one whose clinic is not
  // among the booking clinics (membership ended) grants nothing.
  const grant = !apt.clinicId || Boolean(clinic?.canBookDirectly);
  const writable = !clinic?.readOnly;
  const hasPatient = Boolean(apt.patientId);
  const dayHasCome = isToday || isPast;

  return [
    // The phone has no inline «ثبت حضور»; the desktop row keeps its own button
    // and drops this one (`inline: true`).
    { id: 'arrive', inline: true, show: status === 'scheduled' && isToday && hasPatient && writable },
    { id: 'next', show: status === 'arrived' && isToday && queuePos !== 1 && writable },
    { id: 'backToRoom', show: (status === 'completed' || status === 'no_show') && isToday && hasPatient && writable },
    // «ویرایش نوبت» (PM-0531). A waiting row carries it as its own button (type, time
    // and note in one place); once the patient is here or in the room the time can
    // no longer move, but the visit's type and the note still can, so it lives here.
    { id: 'edit', show: (status === 'arrived' || status === 'in_visit') && writable },
    { id: 'note', show: LIVE_STATUSES.has(status) && writable },
    { id: 'nextAppt', show: hasPatient && canBook },
    { id: 'noShow', danger: true, show: status === 'scheduled' && dayHasCome && grant && writable },
    { id: 'cancel', danger: true, show: (status === 'scheduled' || status === 'arrived') && grant },
  ]
    .filter((o) => o.show)
    .map(({ id, danger = false, inline = false }) => ({ id, danger, inline }));
}

export default receptionRowOps;
